import React, { useContext, useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
  Box,
  Divider,
  Drawer,
  Hidden,
  List,
  makeStyles,
  Typography,
} from "@material-ui/core";
import NavItem from "./NavItem";
import NavItemMenu from "./NavItemMenu";

import UserAvatar from "./UserAvatar";
import { RiHome2Line } from "react-icons/ri";
import { FiLogIn, FiClock } from "react-icons/fi";
import { FaRegImages } from "react-icons/fa";
import { BiHelpCircle, BiMailSend } from "react-icons/bi";
import { useTranslation } from "react-i18next";
import i18n from "../i18n/i18n";
import { languages } from "../i18n/languagesUsed";
import ReactCountryFlag from "react-country-flag";
import { UseVersion } from "../api/version/Version.api";
import { AuthContext } from "../context/auth/AuthContext";
import { useDispatch, useSelector } from "react-redux";
import {
  connectionLineStatusResetState,
  initConnectionLineStatus,
} from "../store/eklok-line-status.store";
import { useMediaQuery } from "../hooks/mediaQuery";

const useStyles = makeStyles((theme) => ({
  mobileDrawer: {
    width: 256,
    background: "#F7F8FA",
  },
  desktopDrawer: {
    width: 256,
    height: "calc(100% - 0px)",
    position: "relative",
    zIndex: "0",
    background: "#F7F8FA",
    borderRight: "1px solid rgba(0, 0, 0, 0.06)",
    boxShadow: "2px 0 12px rgba(0, 0, 0, 0.04)",
  },
  userSection: {
    background:
      "linear-gradient(160deg, rgba(233, 58, 36, 0.08) 0%, rgba(247, 248, 250, 0) 100%)",
    padding: theme.spacing(3, 2, 2),
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    borderBottom: "1px solid rgba(0, 0, 0, 0.06)",
  },
  navSection: {
    padding: theme.spacing(1.5, 1),
    flexGrow: 1,
  },
  versionFooter: {
    padding: theme.spacing(1.5, 2),
    borderTop: "1px solid rgba(0, 0, 0, 0.06)",
  },
  versionText: {
    fontSize: "0.68rem",
    color: theme.palette.text.secondary,
    opacity: 0.55,
    letterSpacing: "0.03em",
    lineHeight: 1.9,
    fontWeight: 400,
  },
}));

const NavBar = ({ onMobileClose, openMobile, user, items }) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const location = useLocation();
  const dispatch = useDispatch();
  const isMobile = useMediaQuery();
  const { eklok, processLine } = useSelector((state) => {
    return state.eklokLineStatus;
  });

  const { token } = useContext(AuthContext);
  const { content: data } = UseVersion(token);

  useEffect(() => {
    if (openMobile && onMobileClose) {
      onMobileClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const changeLanguage = (lng) => {
    localStorage.setItem("language", lng);
    i18n.changeLanguage(lng);
  };

  const setEklokLineStatus = () => {
    if (eklok && isMobile) {
      dispatch(
        initConnectionLineStatus({
          token: token,
          body: { eklok: eklok, line_status: false, processLine: "DO_RESET" },
        }),
      );
    }
  };

  useEffect(() => {
    if (processLine === "DO_RESET" && isMobile) {
      dispatch(connectionLineStatusResetState());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [processLine, isMobile]);

  const content = (
    <Box display="flex" flexDirection="column" height={1}>
      <Box className={classes.userSection}>
        <UserAvatar user={user} />
      </Box>
      <Box className={classes.navSection}>
        <List disablePadding>
          {items.map((item) => (
            <NavItem
              href={item.href}
              key={item.title}
              title={item.title}
              icon={item.icon}
              onClick={setEklokLineStatus}
            />
          ))}
        </List>
      </Box>
      <Box className={classes.versionFooter}>
        <Typography component="div" className={classes.versionText}>
          Frontend: {process.env.REACT_APP_RELEASE_VERSION.replace("noprd.", "")}
        </Typography>
        <Typography component="div" className={classes.versionText}>
          Backend: {data ? data.release_version.replace("noprd.", "") : "—"}
        </Typography>
      </Box>
    </Box>
  );

  const contentNavBar = (
    <Box display="flex" flexDirection="column">
      <Divider />

      <Box p={2}>
        <List>
          <NavItemMenu
            href={t("nav_menuburger.link_home")}
            title={t("nav_menuburger.home")}
            icon={RiHome2Line}
            onClick={setEklokLineStatus}
          />
          <NavItemMenu
            href={t("nav_menuburger.link_reserved_area")}
            title={t("nav_menuburger.reserved_area")}
            icon={FiLogIn}
            onClick={setEklokLineStatus}
          />

          <NavItemMenu
            href={t("nav_menuburger.link_products")}
            title={t("nav_menuburger.products")}
            icon={FiClock}
            onClick={setEklokLineStatus}
          />

          <NavItemMenu
            href={t("nav_menuburger.link_gallery")}
            title={t("nav_menuburger.gallery")}
            icon={FaRegImages}
            onClick={setEklokLineStatus}
          />

          <NavItemMenu
            href={t("nav_menuburger.link_support")}
            title={t("nav_menuburger.support")}
            icon={BiHelpCircle}
            onClick={setEklokLineStatus}
          />

          <NavItemMenu
            href={t("nav_menuburger.link_contact_us")}
            title={t("nav_menuburger.contact_us")}
            icon={BiMailSend}
            onClick={setEklokLineStatus}
          />

          <Divider />

          <Box display="flex" flexDirection="row">
            {languages.map((language, i) => {
              if (i18n.language !== language.key)
                return (
                  <Box m={1}>
                    <ReactCountryFlag
                      onClick={() => changeLanguage(language.key)}
                      countryCode={language.countryCode}
                      svg
                      style={{
                        width: "1.3em",
                        height: "1.3em",
                      }}
                      title={language.countryCode}
                    />
                  </Box>
                );
              return null;
            })}
          </Box>
        </List>
      </Box>
      <Box flexGrow={1} />
    </Box>
  );

  return (
    <>
      <Hidden lgUp>
        <Drawer
          anchor="left"
          classes={{ paper: classes.mobileDrawer }}
          onClose={onMobileClose}
          open={openMobile}
          variant="temporary"
        >
          {content}
          {contentNavBar}
        </Drawer>
      </Hidden>

      <Hidden mdDown>
        <Drawer
          anchor="left"
          classes={{ paper: classes.desktopDrawer }}
          open
          variant="persistent"
        >
          {content}
        </Drawer>
      </Hidden>
    </>
  );
};

export default NavBar;
